/**
 * Shared envelope-unwrap helpers + canonical `ApiError` class.
 *
 * The platform wraps every JSON response (success and error alike) in the
 * envelope shape declared by `ApiEnvelope<T>` (see
 * `@dfq/shared/types/auth`). Per-domain API modules under
 * `@dfq/shared/api/*` need to:
 *
 *   1. Unwrap a 2xx envelope into its typed `data` slot.
 *   2. Translate any 4xx / 5xx envelope into a typed exception that carries
 *      the platform's full error shape — `status`, `severity`, `message`,
 *      and (when present) the `errors` map and `error_code`.
 *
 * Doing both in one shared helper means every API module gets the same
 * error contract for free, so callers (slices, thunks, container effects)
 * can `catch (e instanceof ApiError)` once and consume `e.severity` /
 * `e.errors` / `e.errorCode` uniformly.
 *
 * The helper does NOT touch the in-memory tokenStore or the Redux store —
 * authentication side-effects live in the `auth` module's bespoke unwrap
 * (which mirrors this contract but additionally writes tokens). Keep this
 * file dependency-light; everything here must be importable from any
 * other `@dfq/shared/api/*` module without cycles.
 *
 * CON-PRICE-001.
 */
import type { AxiosError } from 'axios';
import { isAxiosError } from 'axios';

import type { ApiEnvelope } from '@dfq/shared/types/auth';

/**
 * Canonical error type thrown by every `@dfq/shared/api/*` helper that uses
 * `unwrap` (and the per-domain helpers built on top of it).
 *
 * Carries the four platform envelope fields callers care about:
 *  - `status` — the HTTP status code from the response (or `0` when the
 *    request never reached the server, e.g. network failure).
 *  - `severity` — the platform's `severity` field (`info | warning | error
 *    | critical`). Callers use this to route to toast / banner / log /
 *    crash UIs without re-deriving from the status code.
 *  - `errors` — the field-keyed validation map emitted on 422.
 *  - `errorCode` — the platform's machine-readable `error_code` (or
 *    `error.code` when the structured `error` block is populated, e.g. on
 *    a 409 range-overlap conflict). Callers can match on this without
 *    parsing the message.
 */
export type ApiErrorSeverity = 'info' | 'warning' | 'error' | 'critical';

export class ApiError extends Error {
  public readonly status: number;

  public readonly severity: ApiErrorSeverity | undefined;

  public readonly errors: Record<string, string[]> | undefined;

  public readonly errorCode: string | undefined;

  /**
   * Structured context payload from the platform's `error.context` slot.
   * Populated for non-validation Result-style failures (e.g. a 409
   * conflict that needs to attach a `referencedOrderNumbers` list). Left
   * untyped at this layer — the caller knows which endpoint they hit
   * and casts to the expected shape.
   */
  public readonly context: Record<string, unknown> | undefined;

  constructor(params: {
    message: string;
    status: number;
    severity?: ApiErrorSeverity;
    errors?: Record<string, string[]>;
    errorCode?: string;
    context?: Record<string, unknown>;
  }) {
    super(params.message);
    this.name = 'ApiError';
    this.status = params.status;
    this.severity = params.severity;
    this.errors = params.errors;
    this.errorCode = params.errorCode;
    this.context = params.context;
  }
}

/**
 * True when the value is an `ApiError` produced by this module (or any
 * subclass). Useful for `catch (e) { if (isApiError(e)) ... }` flows in
 * containers and slices.
 */
export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError;
}

/**
 * Build an `ApiError` from a server envelope, preserving the platform's
 * full error contract. Exported so per-domain helpers (e.g. ones that need
 * to wrap an envelope they already have in hand, like the auth module's
 * MFA branch) can reuse the construction.
 */
export function apiErrorFromEnvelope(envelope: ApiEnvelope<unknown>, status: number): ApiError {
  return new ApiError({
    message: envelope.message,
    status,
    severity: envelope.severity,
    errors: envelope.errors,
    // `error_code` is the canonical envelope field; the structured
    // `error.code` is populated by the Result-style failure path. Prefer
    // the canonical field but fall back so callers see the code regardless
    // of which terminal the platform used.
    errorCode: envelope.error_code ?? envelope.error?.code,
    // The Handler puts an exception's context under `meta.context` (cart rule
    // violations, declined balances); the Result-style failure path puts it
    // under `error.context`. Callers should not have to know which.
    context:
      envelope.error?.context ??
      (envelope.meta?.context as Record<string, unknown> | undefined),
  });
}

/**
 * Translate any thrown value from an axios call site into an `ApiError`.
 * Distinguishes three cases:
 *
 *  - The error is an axios response error AND the response body is a
 *    well-formed envelope → unwrap into the canonical fields.
 *  - The error is an axios error but the response body is malformed (e.g.
 *    HTML 502 page) → fall back to a generic `ApiError` carrying the
 *    transport status and message.
 *  - The error is non-axios (network failure, programmer error in a
 *    request transformer, etc.) → surface as a `status: 0` `ApiError` so
 *    callers don't have to special-case it.
 *
 * Always rethrows as `ApiError` — never returns. The signature is `never`
 * so TypeScript narrows the `try { unwrap(...) } catch (e) {}` flow
 * correctly on the caller's side.
 */
export function throwAsApiError(error: unknown): never {
  if (isAxiosError(error)) {
    const axiosErr = error as AxiosError<ApiEnvelope>;
    const responseStatus = axiosErr.response?.status ?? 0;
    const envelope = axiosErr.response?.data;
    if (envelope && typeof envelope === 'object' && 'status' in envelope) {
      throw apiErrorFromEnvelope(envelope, responseStatus);
    }
    throw new ApiError({
      message: axiosErr.message || 'Request failed',
      status: responseStatus,
    });
  }

  if (error instanceof Error) {
    throw new ApiError({ message: error.message, status: 0 });
  }

  throw new ApiError({ message: 'Unknown request error', status: 0 });
}

/**
 * Unwrap a successful 2xx envelope into its typed `data` slot. Throws
 * an `ApiError` when the envelope reports `status: "error"` even on a
 * 2xx HTTP code (rare but legal — the platform's error terminals always
 * carry a 4xx/5xx status, so a 2xx-with-error envelope is a bug; we still
 * surface it as an `ApiError` so callers don't silently consume a bad
 * payload).
 *
 * Note: the matching axios call site should let axios's default
 * `validateStatus` reject any non-2xx — `throwAsApiError` then handles
 * that rejection. This helper handles the in-band success path only.
 */
export function unwrap<T>(envelope: ApiEnvelope<T>, status: number): T {
  if (envelope.status !== 'success') {
    throw apiErrorFromEnvelope(envelope as ApiEnvelope<unknown>, status);
  }
  // `data` is optional on the envelope (e.g. 204 No Content), but every
  // call site that uses `unwrap` types `T` to whatever the endpoint
  // documents. We coerce a missing `data` to `{}` so the type assertion
  // matches the documented shape; callers that expect `data` to be
  // populated should use a non-empty `T` and check the field they need.
  return envelope.data ?? ({} as T);
}

/**
 * Project the platform's list envelope into a {@link PaginatedResponse}.
 *
 * The platform nests a collection under a named key in `data` (`data.orders`)
 * and puts pagination under `meta.pagination` on the envelope — not the
 * `{ data, meta }` slot `PaginatedResponse` originally assumed. Reading the
 * envelope directly yields `undefined` for both, which surfaces as
 * `Cannot convert undefined or null to object` inside an entity adapter.
 *
 * Throws the usual API error for a non-success envelope.
 */
export function unwrapList<T>(
  envelope: ApiEnvelope<Record<string, unknown>>,
  status: number,
  collectionKey: string,
): { data: T[]; meta: { current_page: number; per_page: number; total: number; last_page: number } } {
  const data = unwrap(envelope, status);
  const raw = (data as Record<string, unknown>)[collectionKey];
  const items = Array.isArray(raw) ? (raw as T[]) : [];

  const pagination = (envelope.meta?.pagination ?? {}) as Record<string, unknown>;
  const asNumber = (value: unknown, fallback: number): number =>
    typeof value === 'number' && Number.isFinite(value) ? value : fallback;

  return {
    data: items,
    meta: {
      current_page: asNumber(pagination.page, 1),
      per_page: asNumber(pagination.per_page, items.length),
      total: asNumber(pagination.total, items.length),
      last_page: asNumber(pagination.last_page, 1),
    },
  };
}
